import { Fragment, useState } from 'react';
import { InView } from 'react-intersection-observer';

import {
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
} from '@material-ui/core';

import {
  FilterEditorComponentProps,
  SearchUtils,
  SimpleAutocomplete,
  extractLastNameComponent,
  useAlert,
} from '@spotify/confidence-core-react';
import {
  getError,
  getTypeOrNull,
  useFlagsFilterQuery,
} from '@spotify/confidence-graphql';

const Container = styled('div')({
  maxHeight: 300,
  overflowY: 'auto',
});

export const FlagFilterEditor = ({
  value,
  onValueChange,
  onClose,
}: FilterEditorComponentProps<string[]>) => {
  const [query, setQuery] = useState('');
  const alert = useAlert();

  const filter = query
    ? `${SearchUtils.buildSearchFilter(query, 'displayName')} AND state:ACTIVE`
    : 'state:ACTIVE';

  const handleError = (e: Error) => {
    alert.post({ message: e.message, severity: 'error' });
  };

  const { data, fetchMore } = useFlagsFilterQuery({
    variables: {
      filter,
    },
    onCompleted: response => {
      const error = getError(response.flags);
      if (error) {
        handleError(error);
      }
    },
    onError: handleError,
  });

  const nextPageToken = getTypeOrNull(data?.flags)?.nextPageToken;

  const flags: string[] = (getTypeOrNull(data?.flags)?.flags ?? []).map(
    f => f.name,
  );

  return (
    <Container>
      <SimpleAutocomplete.Root
        multiple
        items={flags.map(f => ({
          value: f,
          label: extractLastNameComponent(f),
        }))}
        value={value ?? []}
        onValueChange={onValueChange}
        // when someone selects from the list, close the editor
        onSelect={() => {
          onClose();
          return false;
        }}
        // we do our own filtering
        disableFilter
      >
        {({ items, getInputProps, getListboxProps, getOptionProps }) => (
          <Fragment>
            <SimpleAutocomplete.Input
              data-track-source-anchor="5a1a19bb78" {...getInputProps()}
              placeholder="Search flags..."
              fullWidth
              autoFocus
              margin="dense"
              // we control the component
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <SimpleAutocomplete.Empty />

            <List {...getListboxProps()}>
              {items.map((item, index) => (
                <ListItem
                  key={item.value}
                  button
                  disableGutters
                  {...getOptionProps(index)}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={value?.includes(item.value) ?? false}
                      onClick={e => e.stopPropagation()}
                      onChange={e => {
                        if (e.target.checked) {
                          onValueChange([...(value ?? []), item.value]);
                        } else {
                          onValueChange(
                            value?.filter(v => v !== item.value) ?? [],
                          );
                        }
                      }}
                      tabIndex={-1}
                      disableRipple
                     data-track-source-anchor="70e9d96bcb;flag-filter-editor-checkbox;low"/>
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
              <InView
                onChange={inView => {
                  if (inView && nextPageToken) {
                    fetchMore({
                      variables: { filter, pageToken: nextPageToken },
                    });
                  }
                }}
               data-track-source-anchor="e3bd35309e;flag-filter-editor-in-view"/>
            </List>
          </Fragment>
        )}
      </SimpleAutocomplete.Root>
    </Container>
  );
};
